/**
 * New Report Page
 *
 * Välj datakälla för att skapa ny årsredovisning.
 * Erbjuder tre alternativ:
 * 1. Ladda upp SIE-fil
 * 2. Ange uppgifter manuellt (kommande)
 * 3. Koppla bokföringssystem (kommande)
 *
 * Rapporten skapas först efter lyckad uppladdning.
 * Efter uppladdning navigeras användaren till wizarden.
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  ArrowLeft,
  AlertCircle,
  Loader2,
  FileText,
  Keyboard,
  Link2,
  Sparkles,
  Clock,
  Zap,
  Building2,
  Info,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { companiesApi, reportsApi, type Company, type AnnualReport, type ParsedSieData, type UserInput } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";
import { FiscalYearModal } from "@/components/wizard/FiscalYearModal";
import { createEmptyParsedSieData } from "@/lib/manual-entry";
import { IntegrationSelector } from "@/components/integrations/IntegrationSelector";

// Data source types
type DataSourceType = "sie" | "manual" | "integration";

export function NewReport() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [company, setCompany] = useState<Company | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [companyError, setCompanyError] = useState<string | null>(null);

  // Data source selection - initialize from URL param if present (e.g., after OAuth redirect)
  const [selectedSource, setSelectedSource] = useState<DataSourceType | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get("source");
    if (sourceParam === "integration" || sourceParam === "sie" || sourceParam === "manual") {
      return sourceParam as DataSourceType;
    }
    return null;
  });

  // Clear source param from URL after reading it (keeps URL clean)
  useEffect(() => {
    if (selectedSource && searchParams.get("source")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("source");
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedSource, searchParams, setSearchParams]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Manual entry modal state
  const [showFiscalYearModal, setShowFiscalYearModal] = useState(false);
  const [isCreatingManual, setIsCreatingManual] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadCompany();
    }
  }, [companyId]);

  const loadCompany = async () => {
    if (!companyId) return;

    setIsLoadingCompany(true);
    setCompanyError(null);

    try {
      const response = await companiesApi.get(companyId);

      if (response.success && response.data) {
        setCompany(response.data.company);
      } else {
        setCompanyError(response.error?.message || "Kunde inte ladda företaget");
      }
    } catch (err) {
      console.error("Load company error:", err);
      toast.error("Kunde inte ladda företaget");
      setCompanyError("Ett fel uppstod vid laddning");
    } finally {
      setIsLoadingCompany(false);
    }
  };

  // Helper to check if fiscal year requires K2 2026 eligibility confirmation
  const checkRequiresK2Confirmation = (report: AnnualReport): boolean => {
    const sieData = report.parsedSieData as ParsedSieData | null;
    const userInput = report.userInput as UserInput;

    if (!sieData?.metadata?.fiscalYearStart) return false;

    const startDate = new Date(sieData.metadata.fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    const is2026OrLater = startDate >= cutoffDate;

    const alreadyConfirmed = !!userInput?.k2Eligibility?.confirmedAt;

    return is2026OrLater && !alreadyConfirmed;
  };

  // Auto-upload file when selected - always navigate to wizard after success
  // The wizard handles K2 modal if required (Step1DataSource has useEffect for this)
  const handleFileUpload = useCallback(async (file: File) => {
    if (!companyId) return;

    setSelectedFile(file);
    setUploadError(null);
    setIsUploading(true);

    try {
      const response = await reportsApi.createWithSie(companyId, file);

      if (response.success && response.data) {
        const report = response.data.report;

        // Always navigate to wizard - let it handle K2 modal if required
        // This ensures on refresh, user is in wizard (not stuck on /new-report)
        if (checkRequiresK2Confirmation(report)) {
          // Navigate to step 1 - the K2 modal will auto-open there
          navigate(`/dashboard/wizard/${report.id}/step/1`);
        } else {
          // Navigate directly to step 2 (overview)
          navigate(`/dashboard/wizard/${report.id}/step/2`);
        }
      } else {
        setUploadError(response.error?.message || "Kunde inte skapa årsredovisning");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Ett fel uppstod vid uppladdning");
      setUploadError("Ett fel uppstod vid uppladdning");
    } finally {
      setIsUploading(false);
    }
  }, [companyId, navigate]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0]);
    }
  }, [handleFileUpload]);

  // Handle manual entry - create report with empty ParsedSieData
  const handleManualEntryConfirmed = async (
    fiscalYearStart: string,
    fiscalYearEnd: string
  ) => {
    if (!companyId || !company) return;

    setIsCreatingManual(true);

    try {
      // Create empty ParsedSieData structure for manual entry
      const emptyParsedSieData = createEmptyParsedSieData(
        fiscalYearStart,
        fiscalYearEnd,
        company.name,
        company.organizationNumber
      );

      // First, create an empty report for this company
      const createResponse = await reportsApi.create({
        companyId,
        fiscalYearStart,
        fiscalYearEnd,
      });

      if (createResponse.success && createResponse.data) {
        const report = createResponse.data.report;

        // Now save the manual SIE data to the report
        const sieResponse = await reportsApi.saveManualSieData(
          report.id,
          emptyParsedSieData
        );

        if (sieResponse.success && sieResponse.data) {
          // Check if K2 confirmation is required
          if (checkRequiresK2Confirmation(sieResponse.data.report)) {
            // Navigate to step 1 - the K2 modal will auto-open there
            navigate(`/dashboard/wizard/${report.id}/step/1`);
          } else {
            // Navigate directly to step 2 (overview) to edit data
            navigate(`/dashboard/wizard/${report.id}/step/2`);
          }
        } else {
          toast.error("Kunde inte spara bokföringsdata");
        }
      } else {
        toast.error(createResponse.error?.message || "Kunde inte skapa årsredovisning");
      }
    } catch (err) {
      console.error("Manual entry error:", err);
      toast.error("Ett fel uppstod");
    } finally {
      setIsCreatingManual(false);
    }
  };

  // Handle integration connected - receives Integration object
  const handleIntegrationConnected = (integration: { id: string; provider: string }) => {
    console.log("Integration connected:", integration.id);
    // The IntegrationSelector will show the sync UI next
  };

  // Handle integration sync complete - receives sync response with data
  const handleIntegrationSyncComplete = async (data: unknown) => {
    if (!companyId) return;

    // Parse sync response - API returns { type, data, calculations, financialYear }
    const syncResponse = data as {
      type?: string;
      data?: ParsedSieData;
      calculations?: { keyFigures: ParsedSieData["keyFigures"]; triggeredNotes: string[] };
      financialYear?: { FromDate: string; ToDate: string };
    };

    // Extract ParsedSieData from response
    const sieData = syncResponse?.data;
    if (!sieData?.metadata) {
      toast.error("Kunde inte läsa bokföringsdata");
      return;
    }

    try {
      // Create a report using the synced SIE data
      const fiscalYearStart = sieData.metadata?.fiscalYearStart || "";
      const fiscalYearEnd = sieData.metadata?.fiscalYearEnd || "";

      const createResponse = await reportsApi.create({
        companyId,
        fiscalYearStart,
        fiscalYearEnd,
      });

      if (createResponse.success && createResponse.data) {
        const report = createResponse.data.report;

        // Save the SIE data to the report
        const sieResponse = await reportsApi.saveManualSieData(report.id, sieData);

        if (sieResponse.success && sieResponse.data) {
          toast.success("Bokföringsdata importerad!");

          // Check if K2 confirmation is required
          if (checkRequiresK2Confirmation(sieResponse.data.report)) {
            navigate(`/dashboard/wizard/${report.id}/step/1`);
          } else {
            navigate(`/dashboard/wizard/${report.id}/step/2`);
          }
        } else {
          toast.error("Kunde inte spara bokföringsdata");
        }
      } else {
        toast.error(createResponse.error?.message || "Kunde inte skapa årsredovisning");
      }
    } catch (err) {
      console.error("Integration sync error:", err);
      toast.error("Ett fel uppstod vid import av bokföringsdata");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/octet-stream": [".se", ".si", ".sie"],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  if (isLoadingCompany) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{companyError || "Företaget hittades inte"}</p>
        <Link to="/dashboard/companies" className="btn btn-primary">
          Tillbaka till företag
        </Link>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // Data Source Selection View
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === null) {
    return (
      <>
        <PageMeta
          title={DASHBOARD_PAGE_META.newReport(company.name).title}
          description={DASHBOARD_PAGE_META.newReport(company.name).description}
          noIndex
        />
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Back link */}
          <Link
            to={`/dashboard/companies/${companyId}`}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Tillbaka till {company.name}
          </Link>

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Ny årsredovisning för {company.name}
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Välj hur du vill importera företagets bokföring. Vi stödjer flera olika sätt att få in data
              för att skapa din årsredovisning.
            </p>
          </div>

          {/* Data Source Cards */}
          <div className="grid gap-6 md:grid-cols-3">
            {/* SIE File Upload */}
            <button
              onClick={() => setSelectedSource("sie")}
              className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-primary-500 hover:shadow-lg hover:shadow-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {/* Popular badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-200">
                  <Sparkles size={12} />
                  Populärast
                </span>
              </div>

              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <FileText className="text-white" size={28} />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                SIE-fil
              </h3>

              <p className="text-sm text-gray-600 mb-4 flex-1">
                Exportera en SIE4-fil från ditt bokföringsprogram och ladda upp den här.
                Fungerar med alla svenska bokföringsprogram.
              </p>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock size={14} />
                <span>Ca 2 minuter</span>
              </div>
            </button>

            {/* Automatic Integration */}
            <button
              onClick={() => setSelectedSource("integration")}
              className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            >
              {/* Beta badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <Zap size={12} />
                  Snart tillgänglig
                </span>
              </div>

              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Link2 className="text-white" size={28} />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Koppla bokföringssystem
              </h3>

              <p className="text-sm text-gray-600 mb-4 flex-1">
                Anslut direkt till Fortnox, Visma, Björn Lundén eller Bokio.
                Vi hämtar bokföringen automatiskt.
              </p>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Zap size={14} />
                <span>Automatisk synkronisering</span>
              </div>
            </button>

            {/* Manual Entry */}
            <button
              onClick={() => setSelectedSource("manual")}
              className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Keyboard className="text-white" size={28} />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Ange manuellt
              </h3>

              <p className="text-sm text-gray-600 mb-4 flex-1">
                Fyll i resultat- och balansräkning manuellt.
                Bra för mindre bolag eller om du saknar SIE-fil.
              </p>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock size={14} />
                <span>Ca 15-30 minuter</span>
              </div>
            </button>
          </div>

          {/* Integration providers preview */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-500 mb-4">Stödda bokföringssystem</p>
            <div className="flex items-center justify-center gap-8 opacity-60">
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-gray-400" />
                </div>
                <span className="text-xs text-gray-500">Fortnox</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-gray-400" />
                </div>
                <span className="text-xs text-gray-500">Visma</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-gray-400" />
                </div>
                <span className="text-xs text-gray-500">Björn Lundén</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-gray-400" />
                </div>
                <span className="text-xs text-gray-500">Bokio</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // SIE Upload View
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === "sie") {
    return (
      <>
        <PageMeta
          title={DASHBOARD_PAGE_META.newReport(company.name).title}
          description={DASHBOARD_PAGE_META.newReport(company.name).description}
          noIndex
        />
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Back button */}
          <button
            onClick={() => setSelectedSource(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
          </button>

          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Ladda upp SIE-fil</h1>
            <p className="text-gray-600">
              Exportera en SIE4-fil från ditt bokföringsprogram och ladda upp den för {company.name}.
            </p>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Så exporterar du SIE4:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Fortnox: Inställningar → Importera/Exportera → SIE</li>
                  <li>Spiris: Arkiv → Export → SIE-export</li>
                  <li>Björn Lundén: Arkiv → Skicka/ta emot → Export SIE</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Upload Area */}
          <div className="card">
            {isUploading ? (
              /* Loading state during upload */
              <div className="border-2 border-dashed border-primary-300 bg-primary-50 rounded-xl p-8 text-center">
                <Loader2 className="mx-auto mb-4 text-primary-600 animate-spin" size={48} />
                <p className="text-gray-700 font-medium">Analyserar SIE-fil...</p>
                {selectedFile && (
                  <p className="text-sm text-gray-500 mt-2">{selectedFile.name}</p>
                )}
              </div>
            ) : uploadError ? (
              /* Error state */
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm text-red-600 font-medium">Fel vid uppladdning</p>
                    <p className="text-sm text-red-600">{uploadError}</p>
                  </div>
                </div>
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all border-gray-300 hover:border-primary-400 hover:bg-gray-50"
                >
                  <input {...getInputProps()} />
                  <Upload className="mx-auto mb-4 text-gray-400" size={48} />
                  <p className="text-gray-700 font-medium">Försök igen med en annan fil</p>
                  <p className="text-sm text-gray-500 mt-2">Dra och släpp eller klicka för att välja</p>
                </div>
              </div>
            ) : (
              /* Default dropzone */
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"
                }`}
              >
                <input {...getInputProps()} />
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload
                    className={isDragActive ? "text-primary-600" : "text-gray-400"}
                    size={32}
                  />
                </div>
                <p className="text-lg font-medium text-gray-900 mb-1">
                  {isDragActive
                    ? "Släpp filen här..."
                    : "Dra och släpp din SIE-fil här"}
                </p>
                <p className="text-sm text-gray-500">
                  eller klicka för att välja fil
                </p>
                <p className="mt-4 text-xs text-gray-400">
                  Stödjer SIE4 och SIE4E (.se, .si, .sie)
                </p>
              </div>
            )}
          </div>

          {/* Supported formats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <FileText className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-sm font-medium text-gray-900">SIE4</p>
              <p className="text-xs text-gray-500">Komplett export</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <FileText className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-sm font-medium text-gray-900">SIE4E</p>
              <p className="text-xs text-gray-500">Med verifikationer</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
              <CheckCircle className="mx-auto text-green-500 mb-2" size={24} />
              <p className="text-sm font-medium text-gray-900">Auto-encoding</p>
              <p className="text-xs text-gray-500">CP437, ISO-8859-1, UTF-8</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // Integration View - Uses shared IntegrationSelector component
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === "integration") {
    return (
      <>
        <PageMeta
          title={DASHBOARD_PAGE_META.newReport(company.name).title}
          description={DASHBOARD_PAGE_META.newReport(company.name).description}
          noIndex
        />
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Back button */}
          <button
            onClick={() => setSelectedSource(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Koppla bokföringssystem
            </h1>
            <p className="text-gray-600">
              Anslut ditt bokföringsprogram för att automatiskt importera bokföringsdata.
            </p>
          </div>

          {/* Use the same IntegrationSelector component as the wizard */}
          <IntegrationSelector
            companyId={companyId!}
            onConnected={handleIntegrationConnected}
            onSyncComplete={handleIntegrationSyncComplete}
          />

          {/* Alternative */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-500 mb-3">
              Föredrar du att ladda upp manuellt?
            </p>
            <button
              onClick={() => setSelectedSource("sie")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Ladda upp en SIE-fil istället →
            </button>
          </div>
        </div>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // Manual Entry View
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === "manual") {
    return (
      <>
        <PageMeta
          title={DASHBOARD_PAGE_META.newReport(company.name).title}
          description={DASHBOARD_PAGE_META.newReport(company.name).description}
          noIndex
        />
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Back button */}
          <button
            onClick={() => setSelectedSource(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            disabled={isCreatingManual}
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
          </button>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Ange uppgifter manuellt
            </h1>
            <p className="text-gray-600">
              Fyll i resultat- och balansräkning för hand. Bra för mindre bolag.
            </p>
          </div>

          {/* Start manual entry */}
          <div className="bg-white border-2 border-amber-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Keyboard className="text-amber-600" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Börja med att ange räkenskapsår
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Ange start- och slutdatum för räkenskapsåret. Sedan kan du fylla i
              resultat- och balansräkning i nästa steg.
            </p>
            <button
              onClick={() => setShowFiscalYearModal(true)}
              disabled={isCreatingManual}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingManual ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Skapar rapport...
                </>
              ) : (
                "Ange räkenskapsår"
              )}
            </button>
          </div>

          {/* What you can enter */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              I nästa steg kan du ange:
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                <span className="text-sm text-gray-700">Resultaträkning (K2-format)</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                <span className="text-sm text-gray-700">Balansräkning</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                <span className="text-sm text-gray-700">Jämförelsetal (flerårsöversikt)</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                <span className="text-sm text-gray-700">Notuppgifter</span>
              </div>
            </div>
          </div>
        </div>

        {/* Fiscal Year Modal */}
        <FiscalYearModal
          isOpen={showFiscalYearModal}
          onClose={() => setShowFiscalYearModal(false)}
          onConfirm={handleManualEntryConfirmed}
        />
      </>
    );
  }

  return null;
}
